import { Button, Field, FieldLabel, Input } from "@forge/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Flame } from "lucide-react";
import { useState } from "react";
import { api, unwrap } from "../lib/api";

export const Route = createFileRoute("/reset-password")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: ResetPasswordPage,
});

function apiErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object" && "value" in error) {
    const value = (error as { value: unknown }).value;
    if (
      value &&
      typeof value === "object" &&
      "error" in value &&
      typeof (value as { error: unknown }).error === "string"
    ) {
      return (value as { error: string }).error;
    }
  }
  return "Não foi possível redefinir a senha.";
}

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");

  const reset = useMutation({
    mutationFn: async () =>
      unwrap(await api.auth["reset-password"].post({ token, password })),
    onSuccess: async () => {
      await navigate({ to: "/login", search: { reset: "1" } });
    },
  });

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface p-4">
      <div className="w-full max-w-xs">
        <div className="mb-8 flex flex-col items-center gap-3">
          <Link
            to="/"
            className="flex size-10 items-center justify-center rounded-surface bg-ember text-ink-inverse shadow-raised"
          >
            <Flame className="size-5" />
          </Link>
          <div className="text-center">
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              Nova senha
            </h1>
            <p className="text-2xs text-ink-muted">
              Escolha uma senha com pelo menos 8 caracteres.
            </p>
          </div>
        </div>

        {!token ? (
          <p className="rounded-control border border-danger/25 bg-danger-subtle px-3 py-2 text-2xs text-danger">
            Este link está incompleto. Peça um novo em Esqueci a senha.
          </p>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              reset.mutate();
            }}
          >
            <Field>
              <FieldLabel>Nova senha</FieldLabel>
              <Input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
              />
            </Field>
            {reset.isError && (
              <p className="rounded-control border border-danger/25 bg-danger-subtle px-3 py-2 text-2xs text-danger">
                {apiErrorMessage(reset.error)}
              </p>
            )}
            <Button type="submit" size="lg" loading={reset.isPending}>
              Salvar senha
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-2xs text-ink-muted">
          <Link to="/login" className="text-ink underline-offset-2 hover:underline">
            Voltar ao login
          </Link>
        </p>
      </div>
    </div>
  );
}
